import React, { useState } from 'react';
import { Phone, Mail, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { companyInfo } from '../mock/mockData';

const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError('');
    const form = e.currentTarget;
    const payload = new FormData(form);
    try {
      const response = await fetch('https://formsubmit.co/ajax/zenopharma.co@gmail.com', {
        method: 'POST',
        body: payload,
        headers: { Accept: 'application/json' }
      });
      const result = await response.json();
      if (!response.ok || (result.success !== true && result.success !== 'true')) {
        throw new Error(result.message || 'Unable to send your message right now.');
      }
      form.reset();
      setShowSuccessPopup(true);
    } catch (error) {
      setSubmitError('Your message could not be sent. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-red-50 to-white">
      <div className="container mx-auto px-4">
        {/* Success modal stays on Zeno Pharma; shown only after a confirmed response */}
        {showSuccessPopup && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4" role="presentation" onClick={() => setShowSuccessPopup(false)}>
          <div role="alertdialog" aria-modal="true" aria-labelledby="success-title" className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl p-9 text-center" onClick={e => e.stopPropagation()}>
            <div className="mx-auto mb-5 w-16 h-16 bg-green-50 rounded-full flex items-center justify-center"><CheckCircle className="text-green-600" size={35}/></div>
            <h3 id="success-title" className="text-2xl font-bold text-slate-900 mb-3">Thank you for reaching out!</h3>
            <p className="text-slate-600 mb-7">Your message has been sent successfully. Our team will get back to you as soon as possible.</p>
            <Button type="button" className="bg-red-600 hover:bg-red-700 text-white px-8" onClick={() => setShowSuccessPopup(false)}>Back to Website</Button>
          </div>
        </div>}

        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contact Us
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get in touch with us for inquiries, product information, or partnership opportunities
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Contact us for any queries or concerns related to our products or services. 
                We value your feedback and look forward to hearing from you.
              </p>

              <div className="space-y-4">
                <a 
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-red-50 transition-colors duration-200 group"
                >
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center group-hover:bg-red-200 transition-colors">
                    <Phone className="text-red-600" size={24} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Phone</div>
                    <span className="text-gray-700 font-medium">{companyInfo.phone}</span>
                  </div>
                </a>

                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-red-50 transition-colors duration-200 group"
                >
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center group-hover:bg-red-200 transition-colors">
                    <Mail className="text-red-600" size={24} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Email</div>
                    <span className="text-gray-700 font-medium">{companyInfo.email}</span>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* AJAX submission without leaving website */}
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h3>
            <form 
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              {/* FormSubmit Configuration */}
              <input type="hidden" name="_subject" value="New Contact Form Submission - Zeno Pharma" />
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_template" value="box" />
              <input type="text" name="_honey" style={{ display: 'none' }} />
              
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Your Name *"
                  required
                  className="w-full"
                />
              </div>
              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Your Email *"
                  required
                  className="w-full"
                />
              </div>
              <div>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Your Phone"
                  className="w-full"
                />
              </div>
              <div>
                <Input
                  type="text"
                  name="company"
                  placeholder="Company Name"
                  className="w-full"
                />
              </div>
              <div>
                <Textarea
                  name="message"
                  placeholder="Your Message *"
                  required
                  rows={5}
                  className="w-full resize-none"
                />
              </div>
              {submitError && <p role="alert" className="text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg p-3">{submitError}</p>}
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;